import { useEffect, useMemo, useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { confirmDueOrder } from '../../services/autoConfirmationService';

const { FiClock, FiCheckCircle } = FiIcons;

function formatRemaining(milliseconds) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }

  return `${hours}h ${minutes}m ${seconds}s`;
}

function getConfirmationMessage(order, isExpired) {
  const deadlineLabel = new Date(order.auto_confirmation_at).toLocaleString();

  if (order.delivery_method === 'delivery' && order.delivery_confirmed_at) {
    if (isExpired) {
      return `The delivery was marked delivered. It will automatically be confirmed if not manually confirmed by ${deadlineLabel}. Confirmation is being finalized securely.`;
    }

    return `This delivery was marked delivered. It will automatically be confirmed if not manually confirmed by ${deadlineLabel}.`;
  }

  if (order.status === 'completed') {
    return order.auto_confirmation_status === 'completed'
      ? 'This order was automatically confirmed by the server.'
      : 'This order has been completed.';
  }

  if (isExpired) {
    return `Order will automatically be confirmed if not manually confirmed by ${deadlineLabel}. Confirmation is being finalized securely.`;
  }

  return `Order will automatically be confirmed if not manually confirmed by ${deadlineLabel}.`;
}

export default function AutoConfirmationTimer({ order, onAutoConfirmed }) {
  const deadline = useMemo(
    () => order?.auto_confirmation_at
      ? new Date(order.auto_confirmation_at).getTime()
      : null,
    [order?.auto_confirmation_at]
  );
  const confirmationAttempted = useRef(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    confirmationAttempted.current = false;
  }, [order?.id, order?.auto_confirmation_at, order?.status]);

  useEffect(() => {
    if (
      !deadline
      || (
        order?.status === 'completed'
        && !order?.delivery_confirmed_at
      )
    ) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [deadline, order?.status, order?.delivery_confirmed_at]);

  useEffect(() => {
    const isDue = Boolean(
      deadline
      && now >= deadline
      && order?.status === 'ready'
      && order?.auto_confirmation_status === 'pending'
      && !confirmationAttempted.current
    );

    if (!isDue) return undefined;

    confirmationAttempted.current = true;

    confirmDueOrder(order.id)
      .then((result) => {
        if (result?.success && onAutoConfirmed) {
          onAutoConfirmed();
        }
      })
      .catch((error) => {
        console.warn('Unable to confirm due order:', error);
        confirmationAttempted.current = false;
      });

    return undefined;
  }, [
    deadline,
    now,
    order?.id,
    order?.status,
    order?.auto_confirmation_status,
    onAutoConfirmed
  ]);

  if (!deadline || ['cancelled', 'declined'].includes(order?.status)) {
    return null;
  }

  const isCompleted = order.status === 'completed';
  const isDelivered = order.delivery_method === 'delivery'
    && Boolean(order.delivery_confirmed_at);

  if (isCompleted && order.delivery_method === 'delivery') {
    return null;
  }

  const isExpired = now >= deadline;
  const remaining = formatRemaining(deadline - now);

  if (isCompleted && !isDelivered) {
    return (
      <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex items-start gap-3">
          <SafeIcon
            icon={FiCheckCircle}
            className="mt-0.5 text-xl text-emerald-600"
          />
          <div>
            <h3 className="font-semibold text-emerald-900">
              Order Completed
            </h3>
            <p className="mt-1 text-sm text-emerald-800">
              {getConfirmationMessage(order, false)}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <SafeIcon
          icon={FiClock}
          className="mt-0.5 text-xl text-amber-700"
        />
        <div>
          <h3 className="font-semibold text-amber-950">
            {isExpired
              ? 'Note - Automatic Confirmation'
              : `This order will be automatically confirmed in ${remaining}`}
          </h3>
          <p className="mt-1 text-sm text-amber-900">
            {getConfirmationMessage(order, isExpired)}
          </p>
          <p className="mt-2 text-xs text-amber-800">
            Deadline: {new Date(deadline).toLocaleString()}
          </p>
        </div>
      </div>
    </section>
  );
}